import { useState } from "react";
import { RefreshCw } from "lucide-react";

import { Button, Input } from "@/components/ui";
import type { AttributeReviewValueFixInput } from "@/graphql/generated";
import { usePoolSearch } from "@/hooks/usePoolSearch";
import {
  canPickAgain,
  hasNotChecked,
  valueView,
  type PoolValue,
} from "@/lib/attributeReview/valueView";
import type { ReviewOption } from "./LeafTabs.types";
import { PoolValuePicker } from "./PoolValuePicker";

export type Pool = {
  key: string;
  description?: string | null;
  source: string;
  check?: string | null;
  options?: ReviewOption[];
  values?: { key: string; label: string }[];
};

type Props = {
  canTakePool: boolean;
  pool: Pool | null;
  values: PoolValue[];
  fixes: Record<string, AttributeReviewValueFixInput>;
  setFix: (fix: AttributeReviewValueFixInput) => void;
  undoFix: (storeValue: string) => void;
  choosePool: (key: string) => void;
  refresh: (what: "pool" | "values") => void;
  busy: boolean;
};

export function PoolSection({
  canTakePool,
  pool,
  values,
  fixes,
  setFix,
  undoFix,
  choosePool,
  refresh,
  busy,
}: Props) {
  const [text, setText] = useState("");
  const search = usePoolSearch(text);
  if (!canTakePool && !pool && values.length === 0) return null;
  const choices = search.searching
    ? search.keys
    : (pool?.options ?? []).map((option) => option.key);
  const check =
    pool?.check === "sure"
      ? "✓ sure"
      : pool?.check === "please check"
        ? "⚠ please check"
        : "";
  const live = values.filter((v) => !v.ignored);
  const ignored = values.filter((v) => v.ignored);
  return (
    <div className="space-y-3 mt-4">
      {pool?.source === "existing" ? (
        <p className="text-sm">
          Pool: <b>{pool.key}</b> (fixed by the leaf)
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-sm">
            Pool: <b>{pool?.key ?? "—"}</b> {check}{" "}
            {pool?.description ? `— ${pool.description}` : ""}
          </p>
          <div className="flex gap-2">
            <Input
              aria-label="Search pools"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => refresh("pool")}
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {choices.map((key) => (
              <Button
                key={key}
                size="sm"
                variant="outline"
                disabled={busy || key === pool?.key}
                onClick={() => choosePool(key)}
              >
                {key}
              </Button>
            ))}
          </div>
        </div>
      )}
      {hasNotChecked(values) && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => refresh("values")}
        >
          <RefreshCw className="h-4 w-4" /> Refresh values
        </Button>
      )}
      <ul className="space-y-1">
        {live.map((v) => {
          const fix = fixes[v.store_value];
          const view = valueView(v, Boolean(fix));
          return (
            <li
              key={v.store_value}
              className="flex flex-wrap items-center gap-2 text-sm"
            >
              <span className="font-mono">{v.store_label}</span>
              <span>
                → {fix?.ignored ? "ignored" : (fix?.poolValue ?? v.pool_value)}
              </span>
              <span className="text-xs text-slate-500">({v.match})</span>
              {view.note && (
                <span className="text-xs text-amber-700">{view.note}</span>
              )}
              {fix ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => undoFix(v.store_value)}
                >
                  Undo
                </Button>
              ) : (
                <>
                  {view.actions.includes("pick") && (
                    <PoolValuePicker
                      storeLabel={v.store_label}
                      poolValues={pool?.values ?? []}
                      onPick={(poolValue) =>
                        setFix({ storeValue: v.store_value, poolValue })
                      }
                    />
                  )}
                  {view.actions.includes("keep") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setFix({
                          storeValue: v.store_value,
                          poolValue: v.store_label,
                        })
                      }
                    >
                      Keep "{v.store_label}" as new
                    </Button>
                  )}
                  {view.actions.includes("ignore") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setFix({ storeValue: v.store_value, ignored: true })
                      }
                    >
                      Ignore
                    </Button>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
      {ignored.length > 0 && (
        <div className="text-xs text-slate-500">
          <p className="font-medium">Ignored</p>
          <ul>
            {ignored.map((v) => {
              const fix = fixes[v.store_value];
              return (
                <li
                  key={v.store_value}
                  className="flex flex-wrap items-center gap-2"
                >
                  <span>
                    {v.store_label}
                    {v.ignored_reason ? ` — ${v.ignored_reason}` : ""}
                  </span>
                  {fix && <span>→ {fix.poolValue}</span>}
                  {fix ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => undoFix(v.store_value)}
                    >
                      Undo
                    </Button>
                  ) : (
                    canPickAgain(v) && (
                      <PoolValuePicker
                        storeLabel={v.store_label}
                        poolValues={pool?.values ?? []}
                        onPick={(poolValue) =>
                          setFix({
                            storeValue: v.store_value,
                            poolValue,
                            ignored: false,
                          })
                        }
                      />
                    )
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
